import React, { useState } from "react";
import { Download, FileText, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Automatic lead-magnet banner shown at the end of every blog article.
 * Inline name+email capture → instant playbook PDF download (lead type "playbook").
 */
export default function PlaybookCta() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/playbook", form).then((x) => x.data);
      setDone(r);
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-14 border border-primary/40 bg-primary/5 p-8 md:p-10 max-w-3xl" data-testid="blog-playbook-cta">
      <div className="flex items-start gap-5">
        <div className="w-12 h-12 grid place-items-center bg-primary text-white flex-shrink-0">
          <FileText className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <p className="overline text-primary mb-2">Free download</p>
          <h3 className="font-display text-2xl md:text-3xl leading-tight">The Website Planning Playbook</h3>
          {done ? (
            <div className="mt-4" data-testid="blog-playbook-success">
              <p className="text-muted-foreground mb-4">It's yours, {form.name.split(" ")[0]} — the link is also in your inbox.</p>
              <a href={done.download_url} download data-testid="blog-playbook-download"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary/90 active:scale-95 transition">
                <Download className="w-4 h-4" /> Download the Playbook (PDF)
              </a>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground mt-3 mb-6 leading-relaxed">
                The 7-step framework I use to plan every client website — with printable worksheets.
                Plan your site like a pro before you spend a dollar.
              </p>
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="blog-playbook-form">
                <input
                  required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your name" aria-label="Your name" data-testid="blog-playbook-name"
                  className="bg-background border border-border px-4 py-3 outline-none focus:ring-1 focus:ring-primary text-sm flex-1"
                />
                <input
                  required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="Your email" aria-label="Your email" data-testid="blog-playbook-email"
                  className="bg-background border border-border px-4 py-3 outline-none focus:ring-1 focus:ring-primary text-sm flex-1"
                />
                <button type="submit" disabled={busy} data-testid="blog-playbook-submit"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary/90 active:scale-95 transition disabled:opacity-60 whitespace-nowrap">
                  {busy ? "Sending…" : "Send It to Me"} <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
